import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { AssetUrls } from 'config/assets';
import { useTransferPointsMutation } from 'store/services/points/pointsApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useGetNotificationSettingsQuery } from 'store/services/message/messageApi';
import { playPointsSound } from 'utils/audioManager';
import './SendPointsModal.css';

interface SendPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipientId: number;
  recipientUsername: string;
  tradeId?: string;
}

const SendPointsModal: React.FC<SendPointsModalProps> = ({
  isOpen,
  onClose,
  recipientId,
  recipientUsername,
  tradeId,
}) => {
  const [amount, setAmount] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [transferPoints, { isLoading }] = useTransferPointsMutation();
  const { data: me } = useGetMeQuery();
  const { data: notificationSettings } = useGetNotificationSettingsQuery();
  const soundEnabled = notificationSettings?.soundEnabled ?? true;

  const currentBalance = me?.points ?? 0;

  useEffect(() => {
    if (!isOpen) {
      setAmount('');
      setMessage('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const numericAmount = amount ? parseInt(amount, 10) : 0;
  const newBalance = currentBalance - numericAmount;

  const isValid =
    numericAmount > 0 &&
    !isNaN(numericAmount) &&
    numericAmount <= currentBalance &&
    message.trim().length >= 10 &&
    message.trim().length <= 500;

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setAmount(value);
    }
  };

  const handleSubmit = async () => {
    if (!isValid) return;

    try {
      const result = await transferPoints({
        recipientId,
        amount: numericAmount,
        message: message.trim(),
        tradeId,
      }).unwrap();

      if (result.success) {
        playPointsSound(numericAmount, soundEnabled);
        toast.success(
          `Successfully sent ${numericAmount.toLocaleString()} points to ${recipientUsername}!`
        );
        onClose();
      } else {
        toast.error(result.message || 'Failed to transfer points.');
      }
    } catch (error: any) {
      console.error('Failed to transfer points:', error);
      toast.error(
        error?.data?.message || 'Failed to transfer points. Please try again.'
      );
    }
  };

  const modalContent = (
    <div className="send-points-modal-overlay" onClick={onClose}>
      <div className="send-points-modal" onClick={(e) => e.stopPropagation()}>
        <div className="send-points-modal-header">
          <h2 className="send-points-modal-title">
            <img
              src={AssetUrls.icons.profile.coin}
              alt=""
              className="send-points-title-icon"
            />
            Send Points
          </h2>
          <button className="send-points-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="send-points-modal-content">
          <div className="send-points-info-section">
            <div className="send-points-info-row">
              <span className="send-points-info-label">Recipient:</span>
              <span className="send-points-info-value">{recipientUsername}</span>
            </div>
            <div className="send-points-info-row">
              <span className="send-points-info-label">Your Balance:</span>
              <span className="send-points-info-value send-points-balance-value">
                {currentBalance.toLocaleString()} points
              </span>
            </div>
          </div>

          <div className="send-points-form-group">
            <label htmlFor="send-points-amount" className="send-points-form-label">
              Amount to Send
              <span className="send-points-label-hint">
                (cannot exceed your balance)
              </span>
            </label>
            <input
              id="send-points-amount"
              type="text"
              className="send-points-form-input"
              placeholder="e.g., 500"
              value={amount}
              onChange={handleAmountChange}
              disabled={isLoading}
            />
            {amount && !isNaN(numericAmount) && numericAmount > 0 && (
              <div className={`send-points-balance-preview ${newBalance < 0 ? 'negative' : ''}`}>
                Your New Balance: {newBalance.toLocaleString()} points
                {newBalance < 0 && (
                  <span className="send-points-error-text"> (Insufficient balance)</span>
                )}
              </div>
            )}
          </div>

          <div className="send-points-form-group">
            <label htmlFor="send-points-message" className="send-points-form-label">
              Message
              <span className="send-points-label-required">*</span>
            </label>
            <textarea
              id="send-points-message"
              className="send-points-form-textarea"
              placeholder="Describe why you're sending these points (10-500 characters)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={500}
              disabled={isLoading}
            />
            <CharacterCounter
              currentLength={message.length}
              maxLength={500}
              warningThreshold={450}
            />
            {message.length > 0 && message.length < 10 && (
              <div className="send-points-validation-hint">
                Message must be at least 10 characters
              </div>
            )}
          </div>
        </div>

        <div className="send-points-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? 'Sending...' : 'Send Points'}
            color="purple"
            onPress={handleSubmit}
            isLoading={isLoading}
            isDisabled={!isValid || isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SendPointsModal;
